<script>
import moment from "moment";
import Swal from "sweetalert2";

import Portal from "../../layouts/portal";
import AccesoComercio from "@/components/portal-acceso-comercio";
import { clienteComputed, negocioComputed } from "@/state/helpers";
import { misEventos, guardarEvento, eliminarEvento, subirImagen } from "@/services/negocio";
import { estadoDe, vencimientoTexto } from "@/services/eventos";

/**
 * Las fechas se guardan como texto "DD-MM-YYYY" y las horas como "HH:MM",
 * que es lo que escribe y espera la app (`CrearQuePlanHoy`). El campo de fecha
 * del navegador trabaja en "YYYY-MM-DD", así que hay que traducir en ambos
 * sentidos: guardar en otro formato dejaría el evento invisible en el celular.
 */
const FORMATO_APP = "DD-MM-YYYY";
const FORMATO_INPUT = "YYYY-MM-DD";

const VACIO = {
  nombre: "",
  comercio: "",
  descripcion: "",
  ubicacion: "",
  fechaInicio: "",
  fechaFin: "",
  horaInicio: "",
  horaFin: "",
  imagenUrl: "",
};

export default {
  page: { title: "Mis eventos" },
  components: { Portal, AccesoComercio },
  data() {
    return {
      eventos: [],
      cargando: true,
      error: null,
      mostrarFormulario: false,
      guardando: false,
      subiendoImagen: false,
      editandoId: null,
      formulario: { ...VACIO },
    };
  },
  computed: {
    ...clienteComputed,
    ...negocioComputed,
  },
  methods: {
    estado: estadoDe,
    vence: vencimientoTexto,

    aInput(fechaApp) {
      const m = moment(fechaApp, FORMATO_APP, true);
      return m.isValid() ? m.format(FORMATO_INPUT) : "";
    },
    aApp(fechaInput) {
      const m = moment(fechaInput, FORMATO_INPUT, true);
      return m.isValid() ? m.format(FORMATO_APP) : "";
    },

    nuevo() {
      this.formulario = { ...VACIO, comercio: this.nombreNegocio };
      this.editandoId = null;
      this.error = null;
      this.mostrarFormulario = true;
    },

    editar(evento) {
      this.formulario = {
        nombre: evento.nombre || "",
        comercio: evento.comercio || this.nombreNegocio,
        descripcion: evento.descripcion || "",
        ubicacion: evento.ubicacion || "",
        fechaInicio: this.aInput(evento.fechaInicio),
        fechaFin: this.aInput(evento.fechaFin),
        horaInicio: evento.horaInicio || "",
        horaFin: evento.horaFin || "",
        imagenUrl: evento.imagenUrl || "",
      };
      this.editandoId = evento.id;
      this.error = null;
      this.mostrarFormulario = true;
    },

    async onImagen(evento) {
      const archivo = evento.target.files && evento.target.files[0];
      if (!archivo) return;

      this.subiendoImagen = true;
      try {
        this.formulario.imagenUrl = await subirImagen(archivo, "queplanhoy_imagenes");
      } catch (err) {
        console.error("[portal] error al subir la imagen del evento:", err);
        this.error = "No pudimos subir la imagen. Inténtalo de nuevo.";
      } finally {
        this.subiendoImagen = false;
      }
    },

    async guardar() {
      const f = this.formulario;
      if (!f.nombre.trim() || !f.descripcion.trim() || !f.ubicacion.trim() || !f.fechaInicio) {
        this.error = "Completa nombre, descripción, ubicación y fecha de inicio.";
        return;
      }
      if (f.fechaFin && f.fechaFin < f.fechaInicio) {
        this.error = "La fecha de fin no puede ser anterior a la de inicio.";
        return;
      }
      if (this.subiendoImagen) {
        this.error = "Espera a que termine de subir la imagen.";
        return;
      }

      this.guardando = true;
      this.error = null;

      try {
        await guardarEvento(
          {
            nombre: f.nombre.trim(),
            comercio: f.comercio.trim(),
            descripcion: f.descripcion.trim(),
            ubicacion: f.ubicacion.trim(),
            fechaInicio: this.aApp(f.fechaInicio),
            fechaFin: f.fechaFin ? this.aApp(f.fechaFin) : null,
            horaInicio: f.horaInicio || null,
            horaFin: f.horaFin || null,
            imagenUrl: f.imagenUrl || null,
          },
          this.editandoId
        );

        this.mostrarFormulario = false;
        await this.cargar();
        Swal.fire("Listo", "Tu evento se publicó.", "success");
      } catch (err) {
        console.error("[portal] error al guardar el evento:", err);
        this.error = "No pudimos guardar el evento. Inténtalo de nuevo.";
      } finally {
        this.guardando = false;
      }
    },

    async borrar(evento) {
      const resultado = await Swal.fire({
        title: `¿Eliminar "${evento.nombre}"?`,
        text: "Esta acción no se puede deshacer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!resultado.isConfirmed) return;

      try {
        await eliminarEvento(evento.id);
        await this.cargar();
        Swal.fire("Eliminado", "El evento ya no aparece en Recnet.", "success");
      } catch (err) {
        console.error("[portal] error al eliminar el evento:", err);
        Swal.fire("Error", "No pudimos eliminar el evento.", "error");
      }
    },

    async cargar() {
      this.cargando = true;
      try {
        this.eventos = await misEventos(this.perfilCliente.email);
      } catch (err) {
        console.error("[portal] error al cargar tus eventos:", err);
        this.error = "No pudimos cargar tus eventos.";
      } finally {
        this.cargando = false;
      }
    },
  },
  async mounted() {
    await this.$store.dispatch("negocio/cargar");
    if (this.perfilCliente) await this.cargar();
    else this.cargando = false;
  },
};
</script>

<template>
  <Portal>
    <div class="d-flex justify-content-between align-items-start flex-wrap mb-3">
      <div>
        <h1 class="h3 mb-1">Mis eventos</h1>
        <p class="text-muted mb-0">Lo que publicas en Qué plan hoy.</p>
      </div>
      <b-button v-if="esComercio && !mostrarFormulario" variant="primary" @click="nuevo">
        <i class="mdi mdi-plus"></i> Nuevo evento
      </b-button>
    </div>

    <AccesoComercio>
      <b-alert :show="!!error" variant="danger">{{ error }}</b-alert>

      <!-- Formulario -->
      <div v-if="mostrarFormulario" class="evento-card mb-4">
        <h2 class="h5 mb-4">{{ editandoId ? 'Editar evento' : 'Nuevo evento' }}</h2>

        <b-row>
          <b-col md="6">
            <b-form-group label="Nombre del evento *">
              <b-form-input v-model="formulario.nombre" />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Comercio">
              <b-form-input v-model="formulario.comercio" />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Descripción *">
              <b-form-textarea v-model="formulario.descripcion" rows="3" />
            </b-form-group>
          </b-col>
          <b-col md="12">
            <b-form-group label="Ubicación *">
              <b-form-input v-model="formulario.ubicacion" placeholder="Dónde ocurre" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Fecha de inicio *">
              <b-form-input v-model="formulario.fechaInicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Hora de inicio">
              <b-form-input v-model="formulario.horaInicio" type="time" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Fecha de fin">
              <b-form-input v-model="formulario.fechaFin" type="date" />
            </b-form-group>
          </b-col>
          <b-col md="3">
            <b-form-group label="Hora de fin">
              <b-form-input v-model="formulario.horaFin" type="time" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-form-group label="Imagen"
          description="Cuando termine la fecha y hora de fin, el evento deja de mostrarse automáticamente.">
          <div class="d-flex align-items-center">
            <div class="evento-miniatura mr-3">
              <img v-if="formulario.imagenUrl" :src="formulario.imagenUrl" alt="Imagen del evento" />
              <i v-else class="mdi mdi-image-outline"></i>
            </div>
            <div>
              <input type="file" accept="image/*" @change="onImagen" />
              <p v-if="subiendoImagen" class="text-muted small mb-0 mt-1">Subiendo…</p>
            </div>
          </div>
        </b-form-group>

        <div class="d-flex justify-content-end">
          <b-button variant="secondary" class="mr-2" @click="mostrarFormulario = false">Cancelar</b-button>
          <b-button variant="primary" :disabled="guardando || subiendoImagen" @click="guardar">
            {{ guardando ? 'Guardando…' : 'Publicar' }}
          </b-button>
        </div>
      </div>

      <!-- Listado -->
      <p v-if="cargando" class="text-muted">Cargando tus eventos…</p>
      <p v-else-if="eventos.length === 0" class="text-muted">
        Todavía no has publicado ningún evento.
      </p>

      <div v-else>
        <div v-for="evento in eventos" :key="evento.id" class="evento-card mb-3">
          <div class="d-flex align-items-start flex-wrap">
            <div class="evento-miniatura mr-3">
              <img v-if="evento.imagenUrl" :src="evento.imagenUrl" :alt="evento.nombre" />
              <i v-else class="mdi mdi-calendar-star"></i>
            </div>

            <div class="flex-grow-1">
              <div class="d-flex align-items-center flex-wrap">
                <h3 class="h6 mb-0 mr-2">{{ evento.nombre }}</h3>
                <b-badge :variant="estado(evento).variante">{{ estado(evento).texto }}</b-badge>
              </div>
              <p class="text-muted small mb-1">{{ evento.ubicacion }}</p>
              <p class="text-muted small mb-0">
                <i class="mdi mdi-calendar-clock"></i> Termina: {{ vence(evento) }}
              </p>
            </div>

            <div class="mt-2 mt-md-0">
              <b-button size="sm" variant="outline-primary" class="mr-1" @click="editar(evento)">
                <i class="mdi mdi-pencil"></i>
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="borrar(evento)">
                <i class="mdi mdi-delete"></i>
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </AccesoComercio>
  </Portal>
</template>

<style scoped>
.evento-card {
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 20px;
}

.evento-miniatura {
  width: 80px;
  height: 80px;
  border-radius: 8px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #4b2e83;
  font-size: 28px;
  overflow: hidden;
}

.evento-miniatura img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
